import { useState, useEffect } from 'react'
import { X, Search, Smartphone, Plus } from 'lucide-react'
import { Equipamento, Cliente } from '../../types'
import equipamentosService from '../../services/equipamentos'
import EquipamentoModal from '../equipamentos/EquipamentoModal'
import toast from 'react-hot-toast'

interface EquipamentoSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  cliente: Cliente
  onSelectEquipamento: (equipamento: Equipamento) => void
}

export default function EquipamentoSelectionModal({
  isOpen,
  onClose,
  cliente,
  onSelectEquipamento
}: EquipamentoSelectionModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [equipamentos, setEquipamentos] = useState<Equipamento[]>([])
  const [filteredEquipamentos, setFilteredEquipamentos] = useState<Equipamento[]>([])
  const [selectedEquipamento, setSelectedEquipamento] = useState<Equipamento | null>(null)
  const [loading, setLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)

  useEffect(() => {
    if (isOpen && cliente) {
      loadEquipamentos()
    }
  }, [isOpen, cliente])

  useEffect(() => {
    if (searchTerm) {
      const filtered = equipamentos.filter(equipamento => {
        const searchLower = searchTerm.toLowerCase()
        const marca = equipamento.modelo?.marca?.nome || ''
        const modelo = equipamento.modelo?.nome || ''
        const tipo = equipamento.modelo?.tipoEquipamento?.nome || ''

        return (
          marca.toLowerCase().includes(searchLower) ||
          modelo.toLowerCase().includes(searchLower) ||
          tipo.toLowerCase().includes(searchLower) ||
          equipamento.numeroSerie?.toLowerCase().includes(searchLower) ||
          equipamento.imei?.includes(searchTerm)
        )
      })
      setFilteredEquipamentos(filtered)
    } else {
      setFilteredEquipamentos(equipamentos)
    }
  }, [searchTerm, equipamentos])

  const loadEquipamentos = async () => {
    setLoading(true)
    try {
      const response = await equipamentosService.getByCliente(cliente.id)
      setEquipamentos(response)
      setFilteredEquipamentos(response)
    } catch (error) {
      console.error('Erro ao carregar equipamentos:', error)
      toast.error('Erro ao carregar equipamentos')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectEquipamento = (equipamento: Equipamento) => {
    setSelectedEquipamento(equipamento)
  }

  const handleContinue = () => {
    if (selectedEquipamento) {
      onSelectEquipamento(selectedEquipamento)
      resetModal()
    } else {
      toast.error('Por favor, selecione um equipamento')
    }
  }

  const handleEquipamentoCreated = () => {
    setShowCreateModal(false)
    // Recarregar equipamentos após criar um novo
    loadEquipamentos()
  }

  const resetModal = () => {
    setSearchTerm('')
    setSelectedEquipamento(null)
    setFilteredEquipamentos(equipamentos)
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
          <div className="p-6 border-b">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-semibold">Selecionar Equipamento</h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Cliente: <span className="font-medium">{cliente.nome}</span>
            </p>
          </div>

          <div className="p-6 flex-1 overflow-auto">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium mb-4">O equipamento já existe?</h3>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Pesquisar por marca, modelo, número de série ou IMEI..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : (
                <>
                  {equipamentos.length > 0 ? (
                    <div className="border rounded-lg max-h-60 overflow-y-auto">
                      {filteredEquipamentos.map((equipamento) => (
                        <div
                          key={equipamento.id}
                          onClick={() => handleSelectEquipamento(equipamento)}
                          className={`p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 ${
                            selectedEquipamento?.id === equipamento.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3">
                              <div className="mt-1">
                                <Smartphone className="h-5 w-5 text-gray-400" />
                              </div>
                              <div>
                                <p className="font-medium">
                                  {equipamento.modelo?.marca?.nome} {equipamento.modelo?.nome}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {equipamento.modelo?.tipoEquipamento?.nome}
                                </p>
                                {equipamento.numeroSerie && (
                                  <p className="text-sm text-gray-500">
                                    S/N: {equipamento.numeroSerie}
                                  </p>
                                )}
                                {equipamento.imei && (
                                  <p className="text-sm text-gray-500">
                                    IMEI: {equipamento.imei}
                                  </p>
                                )}
                                {equipamento.cor && (
                                  <p className="text-sm text-gray-500">
                                    Cor: {equipamento.cor}
                                  </p>
                                )}
                              </div>
                            </div>
                            {selectedEquipamento?.id === equipamento.id && (
                              <div className="text-blue-600">
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      Este cliente não possui equipamentos cadastrados
                    </div>
                  )}

                  {searchTerm && filteredEquipamentos.length === 0 && equipamentos.length > 0 && (
                    <div className="text-center py-4 text-gray-500">
                      Nenhum equipamento encontrado
                    </div>
                  )}
                </>
              )}

              <div className="pt-4 border-t">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Plus className="h-5 w-5" />
                  <span>Criar Novo Equipamento</span>
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 border-t flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleContinue}
              disabled={!selectedEquipamento}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedEquipamento
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continuar
            </button>
          </div>
        </div>
      </div>

      <EquipamentoModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleEquipamentoCreated}
        clienteId={cliente.id}
      />
    </>
  )
}